import { Box, Button, Container, HStack, useColorModeValue, Image, Menu, MenuButton, MenuList, MenuItem, Avatar, ButtonGroup, Text } from '@chakra-ui/react';
import { FaUser } from 'react-icons/fa';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { LOGIN_PAGE } from '../utils/authConfig';
import { useEffect, useState } from 'react';

interface UserProfile {
  imageUrl?: string;
  name?: string;
  email?: string;
  givenName?: string;
  familyName?: string;
}

export const Header = () => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const checkAuth = () => {
      const loggedIn = localStorage.getItem('isLoggedIn') === 'true';
      const userProfile = localStorage.getItem('user_profile');
      setIsLoggedIn(loggedIn);
      
      if (userProfile) {
        const parsedProfile = JSON.parse(userProfile);
        console.log('Header - Profile from localStorage:', parsedProfile);
        setProfile(parsedProfile);
      }
    };

    checkAuth();
    
    // Only listen for storage events that affect auth state
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'isLoggedIn' || e.key === 'user_profile' || e.key === null) {
        checkAuth();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    setIsLoggedIn(false);
    setProfile(null);
    navigate(LOGIN_PAGE);
  };

  const getInitials = (profile: UserProfile) => {
    if (profile.givenName && profile.familyName) {
      return `${profile.givenName[0]}${profile.familyName[0]}`;
    }
    if (profile.name) {
      return profile.name.split(' ').map(n => n[0]).join('').toUpperCase();
    }
    return 'U';
  };

  const handleImageError = () => {
    console.error('Avatar image error occurred');
    console.log('Current profile:', profile);
    setImageError(true);
  };

  // Function to determine if a route is active
  const isRouteActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <Box 
      as="header" 
      position="fixed" 
      top={0} 
      left={0} 
      right={0} 
      zIndex={10}
      bg={bgColor}
      borderBottom="1px"
      borderColor={borderColor}
      py={2}
    >
      <Container maxW="container.xl" px={{ base: 2, sm: 4 }}>
        <HStack spacing={{ base: 2, sm: 4 }} justify="space-between">
          {/* Logo and Navigation */}
          <HStack spacing={{ base: 2, sm: 4 }}>
            <Button
              as={RouterLink}
              to="/"
              variant="ghost"
              p={0}
              minW="auto"
              height="auto"
            >
              <Box 
                width={{ base: "100px", sm: "160px" }}
                display="flex"
                alignItems="center"
              >
                <Image 
                  src="/images/podsight-beta.svg" 
                  alt="PodSight Logo" 
                  height={{ base: "28px", sm: "40px" }}
                  objectFit="contain"
                  width="100%"
                />
              </Box>
            </Button>
            <ButtonGroup variant="ghost" spacing={{ base: 1, sm: 2 }}>
              <Button 
                as={RouterLink} 
                to="/for-you"
                bg={isRouteActive('/for-you') ? 'gray.100' : 'transparent'}
                _hover={{ bg: isRouteActive('/for-you') ? 'gray.200' : 'gray.100' }}
                px={{ base: 2, sm: 4 }}
                fontSize={{ base: "sm", sm: "md" }}
              >
                For You
              </Button>
              <Button 
                as={RouterLink} 
                to="/discover"
                bg={isRouteActive('/discover') ? 'gray.100' : 'transparent'}
                _hover={{ bg: isRouteActive('/discover') ? 'gray.200' : 'gray.100' }}
                px={{ base: 2, sm: 4 }}
                fontSize={{ base: "sm", sm: "md" }}
              >
                Discover
              </Button>
            </ButtonGroup>
          </HStack>

          {/* User Menu */}
          {isLoggedIn ? (
            <Menu>
              <MenuButton
                as={Button}
                variant="ghost"
                rightIcon={
                  profile?.imageUrl && !imageError ? (
                    <Avatar 
                      size="sm" 
                      src={profile.imageUrl}
                      onError={handleImageError}
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <Avatar 
                      size="sm" 
                      name={profile ? getInitials(profile) : 'U'}
                      bg="blue.500"
                      color="white"
                    />
                  )
                }
              >
                <Text display={{ base: 'none', md: 'block' }}>
                  {profile?.name || 'User'}
                </Text>
              </MenuButton>
              <MenuList>
                <MenuItem onClick={handleLogout}>Sign Out</MenuItem>
              </MenuList>
            </Menu>
          ) : (
            <Button
              as={RouterLink}
              to={LOGIN_PAGE}
              colorScheme="blue"
              variant="solid"
              leftIcon={<FaUser />}
            >
              Sign In
            </Button>
          )}
        </HStack>
      </Container>
    </Box>
  );
}; 